import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = () => {
    if (!username || !password) {
      setError(true);
      return;
    }

    setIsLoading(true);
    // Simulate API delay (remove in real app if using real auth)
    setTimeout(() => {
      if (username === "Demo" && password === "19protocol") {
        const info = { username, password };
        localStorage.setItem("info", JSON.stringify(info));
        localStorage.setItem("isLoggedIn", "true");
        setError(false);
        navigate('/dashboard');
      } else {
        setError(true);
        setIsLoading(false);
      }
    }, 600);
  };

  return (
    <div style={styles.gradientBackground}>
      <div style={styles.container}>
        {/* Left Section: Logo */}
        <div style={styles.leftSection}>
          <div style={styles.logoWrapper}>
            <img
              src="/assets/img/TreeshawCrop.jpeg"
              alt="Treeshaw Logo"
              style={styles.logo}
            />
          </div>
        </div>

        {/* Right Section: Login Form */}
        <div style={styles.rightSection}>
          <div style={styles.card}>
            <div style={styles.header}>
              <h1 style={styles.title}>Welcome Back</h1>
              <p style={styles.subtitle}>Sign in to your account</p>
            </div>

            <div style={styles.form}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                value={username}
                onChange={e => {
                  setUsername(e.target.value);
                  setError(false);
                }}
                style={{
                  ...styles.input,
                  borderColor: error ? '#ef4444' : '#d1d5db',
                  boxShadow: error ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(209, 213, 219, 0)',
                }}
                placeholder="Enter your username"
                disabled={isLoading}
              />

              <label style={styles.label}>Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    setError(false);
                  }}
                  style={{
                    ...styles.input,
                    borderColor: error ? '#ef4444' : '#d1d5db',
                    boxShadow: error ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(209, 213, 219, 0)',
                    paddingRight: '40px',
                  }}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  style={styles.eyeButton}
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '👁️‍🗨️' : '👁️'}
                </button>
              </div>

              {error && (
                <div style={styles.error}>
                  Invalid username or password
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={isLoading}
                style={{
                  ...styles.loginButton,
                  opacity: isLoading ? 0.85 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span style={styles.spinner}></span>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.poweredBy}>
              Powered by 
              <a href="https://biznweb.in/" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: '#475569', textDecoration:'none'}}><strong>bizNweb</strong></a>
            </p>
          </div>
        </div>
      </div>

      {/* Global Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .animate-fade-in {
          animation: fadeIn 0.6s ease forwards;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-shake {
          animation: shake 0.4s ease;
        }
        @media (max-width: 900px) {
          .login-container {
            flex-direction: column;
            width: 95vw;
            height: auto;
            margin: 20px;
          }
          .left-section {
            width: 100%;
            height: 200px;
          }
          .right-section {
            width: 100%;
            padding: 30px 20px !important;
            alignItems: 'center' !important;
          }
          .login-card {
            width: 100% !important;
            max-width: 420px;
            margin: 0 auto;
          }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  gradientBackground: {
    minHeight: '100vh',
    width: '100vw',
    background: 'linear-gradient(120deg, #fff 0%, #0f7feeff 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    boxSizing: 'border-box',
  },
  container: {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    width: '85vw',
    height: '85vh',
    overflow: 'hidden',
    maxWidth: '1200px',
    animation: 'fadeIn 0.8s ease',
  },
  leftSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#80b7eeff',
    position: 'relative',
    overflow: 'hidden',
  },
  logoWrapper: {
    textAlign: 'center',
    animation: 'float 3s ease-in-out infinite',
  },
  logo: {
    height: '140px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease',
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '0 50px',
    background: '#fff',
  },
  card: {
    width: '420px',
    background: '#fff',
    borderRadius: '16px',
    padding: '40px 32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 155,
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    color: '#1e293b',
    lineHeight: 1.2,
  },
  subtitle: {
    margin: '8px 0 0',
    fontSize: '16px',
    color: '#64748b',
    fontWeight: 400,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontWeight: 600,
    marginTop: '16px',
    marginBottom: '8px',
    color: '#334155',
    fontSize: '14px',
  },
  input: {
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '10px',
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.25s ease',
    width: '100%',
    boxSizing: 'border-box',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '4px',
    borderRadius: '4px',
    transition: 'background 0.2s',
  },
  error: {
    color: '#ef4444',
    fontSize: '14px',
    
    marginTop: '4px',
    marginBottom: '12px',
    fontWeight: 500,
    animation: 'shake 0.5s ease',
  },
  loginButton: {
    marginTop: '35px',
    padding: '14px',
    background: 'linear-gradient(to right, #1e40af, #21a7dd)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontWeight: 700,
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  footer: {
    marginTop: 'auto',
    textAlign: 'center',
    paddingTop: '20px',
  },
  poweredBy: {
    fontSize: '14px',
    color: '#475569',
    margin: 0,
  },
};

export default Login;